import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { MissingResourceError, trackError, ValidationError } from '#api/errors.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { NotFound } from '#screens/404/NotFound.tsx';
import { OopsPage } from '#screens/errors/OopsPage.tsx';
import { SSODisabled } from '../disabled/SSODisabled.tsx';

/**
 * This component handles the start of the OIDC flow, calling our `startOIDC` auth
 * method which will get a url for the identity provider from the api server,
 * and redirect to that url.
 */
export function LoginOIDC() {
  const { orgID } = useParams() as { orgID: string };
  const [error, setError] = useState<Error | undefined>();
  const { startOIDC, clearOIDCLoginURL } = useAuth();
  const { mutate: startOIDCMutation } = useMutation({
    mutationFn: (props: { orgID: string }) => startOIDC(props.orgID),
    onError: (err) => {
      // SSO is feature flagged for this account
      if (err instanceof MissingResourceError) {
        clearOIDCLoginURL();
      }
      // Was the identity provider deleted/disabled?
      else if (ssoIsNotConfigured(err)) {
        // Clear this so that we no longer show the "Login with SSO" button
        clearOIDCLoginURL();
      }
      // Something unexpected happened
      else {
        trackError(err);
      }
      if (err instanceof Error) {
        setError(err);
      }
    },
  });

  useEffect(() => {
    startOIDCMutation({ orgID });
  }, [orgID, startOIDCMutation]);

  // This only happens if the feature flag is disabled,
  // in which case the customer shouldn't know about the login oidc routes.
  if (error instanceof MissingResourceError) {
    return <NotFound />;
  }

  if (ssoIsNotConfigured(error)) {
    return <SSODisabled />;
  }

  if (error) {
    return <OopsPage error={error} showErrorDetails={true} />;
  }

  // No UI to show here, just waiting for a redirect inside startOIDC.
  return null;
}

function ssoIsNotConfigured(error: unknown): boolean {
  return error instanceof ValidationError && error.errors.some((err) => err.code === 'ERR_OIDC_NOT_CONFIGURED');
}
